import { sValidation, IRule } from './ValidationModule';
import { Mod10, Mod11 } from '@/utils/checksum';
import dayjs from '@/utils/dayjs';
import Decimal, { isDecimal } from '@/utils/decimal';

export const allowEmpty = (rule: IRule): IRule => {
  const newRule: IRule = {
    ...rule,
    validation: (args) => {
      if (args.dataValue == null || args.dataValue === ``) return true;
      return rule.validation(args);
    },
  };
  return newRule;
};

const emailregex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validateType = (type: string): sValidation => {
  return ({ dataValue }) => {
    // null is valid for string, date and int types as a way to unset something
    if (dataValue === null) return true;
    if (dataValue === '') return true;
    if (type === 'uint') return /^\d+$/.test(dataValue);
    if (type === 'int') return /^[-−]?\d+$/.test(dataValue);
    if (type === 'unumber') return /^[\d,\s.]+$/.test(dataValue);
    if (type === 'number') return /^[-−]?[\d\s,.]+$/.test(dataValue);
    if (type === 'string') return typeof dataValue === 'string';
    if (type === 'date') return dayjs.strict(dataValue).isValid();
    if (type === 'email') return emailregex.test(dataValue);
    return false;
  };
};

export const validateFormat = (format: RegExp): sValidation => {
  return ({dataValue}) => format.test(dataValue ? dataValue.toString() : '');
};

export const validateMin = (
  minNum: number | Decimal,
): sValidation => {
  return (args) => {
    const value = args.dataValue;
    if (isDecimal(value)) return (value as Decimal).greaterThanOrEqualTo(minNum);
    if (typeof value === 'number') return value >= minNum;
    if (typeof value === 'string') return value.trim().length >= minNum;
    if (Array.isArray(value)) return value.length >= minNum;
    if (value instanceof FileList) return value.length >= minNum;
    return false;
  };
};

export const validateMax = (
  maxNum: number | Decimal,
): sValidation => {
  return (args) => {
    const value = args.dataValue;
    if (isDecimal(value)) return (value as Decimal).lessThanOrEqualTo(maxNum);
    if (typeof value === 'number') return value <= maxNum;
    if (typeof value === 'string') return value.trim().length <= maxNum;
    if (Array.isArray(value)) return value.length <= maxNum;
    if (value instanceof FileList) return value.length <= maxNum;
    return false;
  };
};

export const validateLength = (length: number): sValidation => {
  return (args) => {
    const value = args.dataValue;
    if (typeof value === 'object') return true;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') return value.trim().length === length;
    if (Array.isArray(value)) return value.length === length;
    if (value instanceof FileList) return value.length === length;
    return false;
  };
};

export const validateRequired = (): sValidation => {
  return (args) => {
    const value = args.dataValue;
    if (typeof value === 'boolean') return value;
    if (isDecimal(value)) return !(value as Decimal).isZero();
    if (typeof value === 'number') return !!value;
    if (typeof value === 'string') return !!value.trim().length;
    if (Array.isArray(value)) return !!value.length;
    if (value instanceof FileList) return !!value.length;
    return false;
  };
};

export const validateBetween = (minNum: number, maxNum: number): sValidation => {
  return (args) => {
    return validateMin(minNum)(args) && validateMax(maxNum)(args);
  };
};

export const validateInclusion = (values: number | string | boolean | Array<number | string | boolean>): sValidation => {
  return ({ dataValue }) => {
    if (Array.isArray(values)) {
      if (isDecimal(dataValue)) {
        return values.some(value =>
          [ 'string', 'number' ].includes(typeof value) &&
          (dataValue as Decimal).eq(value as string | number),
        );
      }
      if ([ 'boolean', 'number' ].includes(typeof dataValue)) return values.includes(dataValue);
      if (typeof dataValue === 'string') return values.every(val => dataValue.includes(String(val)));
      if (Array.isArray(dataValue)) return dataValue.every(val => values.includes(val));
    } else {
      if (isDecimal(dataValue)) {
        return (
          [ 'string', 'number' ].includes(typeof values) &&
          (dataValue as Decimal).eq(values as string | number)
        );
      }
      if ([ 'boolean', 'number' ].includes(typeof dataValue)) return dataValue === values;
      if (typeof dataValue === 'string') return dataValue.includes(String(values));
      if (Array.isArray(dataValue)) return dataValue.includes(values);
    }

    return false;
  };
};

export const validateExclusion = (values: number | string | boolean | Array<number | string | boolean>): sValidation => {
  return ({ dataValue }) => {
    if (Array.isArray(values)) {
      if (isDecimal(dataValue)) {
        return !values.some(value =>
          [ 'string', 'number' ].includes(typeof value) &&
          (dataValue as Decimal).eq(value as string | number),
        );
      }
      if ([ 'boolean', 'number' ].includes(typeof dataValue)) return !values.includes(dataValue);
      if (typeof dataValue === 'string') return values.every(val => !dataValue.includes(String(val)));
      if (Array.isArray(dataValue)) return dataValue.every(val => !values.includes(val));
    } else {
      if (isDecimal(dataValue)) {
        return !(
          [ 'string', 'number' ].includes(typeof values) &&
          (dataValue as Decimal).eq(values as string | number)
        );
      }
      if ([ 'boolean', 'number' ].includes(typeof dataValue)) return dataValue !== values;
      if (typeof dataValue === 'string') return !dataValue.includes(String(values));
      if (Array.isArray(dataValue)) return !dataValue.includes(values);
    }

    return false;
  };
};

export const validateMod10 = ({
  allowAnyFormat = false,
  format = (dataValue: number | string) => dataValue,
} = {}): sValidation => {
  return ({ dataValue }) => {
    if (![ 'number', 'string' ].includes(typeof dataValue)) return true;
    if (dataValue === '') return true;

    if (allowAnyFormat) {
      dataValue = String(dataValue).replace(/[\s-]/g, '');
    } else {
      dataValue = format(dataValue);
    }

    return Mod10.isValid(dataValue);
  };
};

export const validateMod11 = ({
  allowAnyFormat = false,
  format = (dataValue: number | string) => dataValue,
  weights = Mod11.weights,
} = {}): sValidation => {
  return ({ dataValue }) => {
    if (![ 'number', 'string' ].includes(typeof dataValue)) return true;
    if (dataValue === '') return true;

    if (allowAnyFormat) {
      dataValue = String(dataValue).replace(/[\s-]/g, '');
    } else {
      dataValue = format(dataValue);
    }

    return Mod11.isValid(dataValue, weights);
  };
};
